import { Component, OnInit, HostListener, ElementRef, ViewChild, Input, EventEmitter, Output, inject, computed, DoCheck, signal } from '@angular/core';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { faBars } from '@fortawesome/pro-solid-svg-icons';
import { UntilDestroy } from '@ngneat/until-destroy';

import { NavRoute } from '../../../shared/navigation/nav.route.interface';
import { SecurityService } from './../../security/security.service';
import { UserJsVm } from '../../../shared/generated/Admin/Models/User/UserJsVm';
import { NavigationService, NavDividerCharacter } from '../../../shared/navigation/navigation.service';
import { TopClickNavComponent } from '../../../shared/navigation/top-click-nav/top-click-nav.component';
import { SearchNavComponent } from '../../../shared/navigation/search-nav/search-nav.component';
import { pcgSettings } from '../../../shared/generated/pcg-settings';
import { GlobalService } from '../../../shared/service/global.service';
import { GlobalVariablesService } from '../../../services/global-variables.service';

@UntilDestroy()
@Component({
    selector: 'pcg-header'
    , templateUrl: './header.component.html'
    , styleUrls: ['./header.component.scss']
    , standalone: false
})
export class TopHeaderComponent implements OnInit, DoCheck {

	@ViewChild(TopClickNavComponent, { static: true }) nav!: TopClickNavComponent;
	@ViewChild('userNav') userNav!: ElementRef;
	@ViewChild('mainHamburger', { static: true }) mainHamburger!: ElementRef;

	@Input() tabPanel: any;
	@Input() onChangePasswordScreen!: boolean;

	@Output() hamburgerClick = new EventEmitter<boolean>();

	user!: UserJsVm;
	
	navRoutes!: NavRoute[];
	currSelectedNavMenu!: NavRoute;

	currForceSelect!: string;
	currMainNavMenu!: string;	
	mode = pcgSettings.mode;	
	navDividerCharacter = NavDividerCharacter;
    currentUrl: string = "";

	userNavOpen = false;
	adminAccess = false;
	isMobile = signal<boolean>(false);
	isLoggedOn!: boolean;
	isNavFixed!: boolean;
	loading = false;
    isProduction = false;

	navigationOffset = 0;

	faIconName = { faBars };

	navService = inject(NavigationService);

	constructor(
		private sec: SecurityService
		, private modalService: NgbModal
		, public globalService: GlobalService
		, private gvs: GlobalVariablesService
	) { }

	@HostListener('window:resize') onResize() { this.isMobile.set(GlobalService.setIsMobile(window.innerWidth)); }

	// Close the main navigation when they click outside of it
	// and are not clicking the mobile hamburger button
	@HostListener('document:click', ['$event']) clickDoc(event: any) {
		if (
			!this.mainHamburger?.nativeElement.contains(event.target) 
			&& !this.nav?.elRef?.nativeElement.contains(event.target) 
			&& !this.userNav?.nativeElement.contains(event.target)
		) {
			this.navService.setCurrOpenNavMenu('');
			this.userNavOpen = false;
		}
	}

	// Open search when they hit the '/' (or '`') key
	@HostListener('document:keydown', ['$event']) onKeydown(event: KeyboardEvent) {
		if (
			(
				event.key === '/' 
				|| event.key === '`'
			) 
			&& !this.isFormField(event.target)
		) {
			event.preventDefault();
			if (!this.modalService.hasOpenModals()) { this.modalService.open(SearchNavComponent); }
		}
	}

	ngOnInit() {
        this.currentUrl = globalThis.location.href;
        if (
            this.currentUrl.startsWith('https://app.schedule2.it') 
            || this.currentUrl.startsWith('https://app1.schedule2.it')
            || this.currentUrl.startsWith('https://app3.schedule2.it')
            || this.currentUrl.startsWith('https://app4.schedule2.it')
            || this.mode.toLowerCase() === 'production'
        ) { this.isProduction = true; } 
        else { this.isProduction = false; }
		this.isMobile.set(GlobalService.setIsMobile(window.innerWidth));
		this.sec.user$.subscribe(user => { this.user = user; });
		setTimeout(() => {
			let secondaryNav = document.getElementById("secondaryNav");
			if (secondaryNav != null) { secondaryNav.scrollLeft = Number(localStorage?.getItem("subNavPosition")); }
		}, 250);

		this.isNavFixed = localStorage.getItem("isNavFixed") === "true";
	}

	ngDoCheck() { this.isLoggedOn = this.sec.isLoggedOn(); } // Needed to check if user is logged in (to toggle header)
	setSubNavPosition() { localStorage.setItem("subNavPosition", document.getElementById("secondaryNav")?.scrollLeft?.toString()!); }
	toggleNav() { this.hamburgerClick.emit(true); } // Toggle whether or not the main navigation is open
	
	// Used in the keyup function belew to prevent bringing up the search if they are focused in a form field
	isFormField(e: any) {
		if (!(e instanceof HTMLElement)) { return false; }
		const t = e.nodeName?.toLowerCase();
		const n = (e.getAttribute('type') || '')?.toLowerCase();
		return (
			'select' === t 
			|| 'textarea' === t 
			|| (
				'input' === t 
				&& 'submit' !== n 
				&& 'reset' !== n
			) 
			|| e.isContentEditable
		);
	}	

	setDynamicStyle(
		name: string
		, styles: string
	) {
		let dynamicStyle = document.getElementById(name);
		if (dynamicStyle) { document.head.removeChild(dynamicStyle); }
		dynamicStyle = document.createElement('style');
		dynamicStyle.id = name;
		dynamicStyle.innerHTML = styles;
		document.head.appendChild(dynamicStyle);
	}

	mainNavTitle = computed(() => {
		let name = this.navService.currNav()?.name;

		switch (name) {
			case "Edit User": { name = this.gvs.userName(); break; }
			case "Edit Supplier": { name = this.gvs.supplierName(); break; }
            case "Edit Department": { name = this.gvs.departmentName(); break; }
            case "Edit Out of Service Location": { name = this.gvs.outOfServiceName(); break; }
			case "View State": { name = this.gvs.stateName(); break; }
			case "Edit Inventory Action": { name = this.gvs.inventoryActionName(); break; }
			case "Customers": { name = this.gvs.sysAdminClientName(); break; }
			case "Edit Integration": { name = this.gvs.integrationName(); break; }
			case "Integrations": { name = this.gvs.integrationName(); break; }
			case "Edit Product": { name = this.gvs.productName(); break; }
            case "Edit Inventory": { name = this.gvs.siteName(); break; }
			case "Settings": { name = this.gvs.clientName(); break; }
			case "Products": { name = this.gvs.siteName(); break; }
            case "First Time Setup": { name = this.gvs.siteName(); break; }
            case "Edit Box Insert": { name = this.gvs.boxInsertSiteName(); break; }
			default: { break; }
		}

		if (name?.length <= 0) { name = this.navService.currNav()?.name; }
		return name;
	});
}
